import { createTheme } from '@mui/material/styles';
import type { Shadows } from '@mui/material/styles';
import { PALETTE, type ThemeMode } from './theme/palette';

/**
 * Alert severity, in tokens.
 *
 * Kept out of `styleOverrides` on purpose. MUI v9 emits an Alert's severity
 * styling through the component's OWN `variants` array, which is appended
 * after `styleOverrides` in the same emotion sheet and therefore wins on order
 * — the same trap documented on MuiAutocomplete below. A theme-level `variants`
 * entry is appended after both, so it is the only slot that actually lands.
 * `styleOverrides.standardError` and friends are not even valid keys in v9.
 */
const ALERT_SEVERITY = {
  error: { fg: 'var(--red)', tint: 'var(--red-tint)', line: 'var(--red-line)' },
  warning: { fg: 'var(--amber)', tint: 'var(--amber-tint)', line: 'var(--amber-line)' },
  success: { fg: 'var(--green)', tint: 'var(--green-tint)', line: 'var(--green-line)' },
  info: { fg: 'var(--acc)', tint: 'var(--acc-tint)', line: 'var(--acc-line)' },
} as const;

const alertVariants = Object.entries(ALERT_SEVERITY).flatMap(([severity, t]) => [
  // standard and outlined: severity is carried by the icon and the hairline,
  // and the message stays --tx-hi like every other body string in the console.
  {
    props: (p: { severity?: string; variant?: string }) =>
      p.severity === severity && p.variant !== 'filled',
    style: {
      color: 'var(--tx-hi)',
      borderColor: t.line,
      '& .MuiAlert-icon': { color: t.fg },
    },
  },
  {
    props: (p: { severity?: string; variant?: string }) =>
      p.severity === severity && p.variant === 'standard',
    style: { backgroundColor: t.tint },
  },
  /*
   * `filled` takes the solid semantic colour as its fill, and the legible text on
   * that fill is exactly what --on-accent names: the canvas on dark, where the
   * semantics are lift colours, and white on light, where they are dark fills.
   * This was `var(--ink-900)`, which encoded the dark answer as if it were the
   * only one.
   */
  {
    props: (p: { severity?: string; variant?: string }) =>
      p.severity === severity && p.variant === 'filled',
    style: { backgroundColor: t.fg, color: 'var(--on-accent)' },
  },
]);

/**
 * Mintly Control Center — dark CRM theme.
 *
 * Palette entries use real hexes (MUI needs them for contrast/alpha math);
 * component styleOverrides reference the CSS custom properties defined in
 * `styles/design-system.scss` so the theme stays token-driven. Keep both in
 * sync — the hexes below mirror the --ink / --tx / --acc / semantic tokens.
 */
/*
 * NOTE: MUI's typography variants below and the --font-size-* scale in design-system.scss are two
 * statements of the same thing, and they had drifted: body2 and button were 12.5px, h3 19px, h1
 * 25.6px — none of them a step on the scale. They now reference the tokens, so there is one scale
 * with one definition. Snapping moved text by at most 1.6px; the e2e design-consistency group
 * asserts button and chip specs against computed style, which is what verifies it.
 */
/*
 * The palette section, built from the token mirror rather than from 31 loose literals.
 *
 * These have to be real colours: MUI runs getContrastRatio, alpha and lighten over them while the
 * theme is constructed and cannot resolve a var(). That is also what makes it worth doing, because
 * it is precisely why the ~27 `sx` call sites naming a palette path follow the palette for free.
 *
 * Guardrail 5c compares theme/palette.ts against the stylesheet, so these values can no longer
 * drift from the ones the browser paints. Nothing checked them before.
 */
/**
 * Build the MUI theme for one mode.
 *
 * A factory rather than the module-level singleton this used to be, because `palette.mode` drives
 * MUI's own derivations -- `action.hover`, `action.selected`, the Alert and Chip internals, the
 * contrast text it computes when you do not pin one -- and none of those can be re-pointed by a
 * CSS custom property. The components block below is a different story: it is 187 `var()`
 * references and follows a theme change on the same paint, with no rebuild at all.
 */
export function createAppTheme(mode: ThemeMode) {
const t = PALETTE[mode];

return createTheme({
  palette: {
    mode,
    primary: {
      main: t['--brand'],       // brand fill (white text — AAA)
      dark: t['--brand-hi'],    // white 5.22:1
      light: t['--acc'],        // on-dark lift
      contrastText: t['--on-brand'],
    },
    secondary: {
      main: t['--teal'],
      dark: t['--teal-hi'],
      light: t['--teal-tint'],
      contrastText: t['--on-accent'],
    },
    /*
     * The four semantic entries. Two things about them are easy to trip over.
     *
     * `main` is an on-dark LIFT colour: a light salmon, a light green. It is
     * meant to be read AS text against the ink canvas, not used as a fill. Any
     * component that fills with it needs dark text on top, which is why every
     * one below pins `contrastText` to --on-accent. Left unset, MUI computes it,
     * and a computed answer is one palette tweak away from silently flipping to
     * white and failing contrast on a filled chip. That token now says which
     * colour rides a filled semantic, in one place, per theme.
     *
     * `light` is a 10%-alpha TINT, not a lighter shade, because that is what
     * the chip and badge fills want. Anything that derives a colour from it
     * inherits the alpha: see the MuiAlert block for what that cost.
     */
    error:   { main: t['--red'],   dark: t['--red'],   light: t['--red-tint'],   contrastText: t['--on-accent'] },
    warning: { main: t['--amber'], dark: t['--amber'], light: t['--amber-tint'], contrastText: t['--on-accent'] },
    success: { main: t['--green'], dark: t['--green'], light: t['--green-tint'], contrastText: t['--on-accent'] },
    info:    { main: t['--acc'],   dark: t['--acc'],   light: t['--acc-tint'],   contrastText: t['--on-accent'] },
    text: {
      primary: t['--tx-hi'],
      secondary: t['--tx-mid'],
      disabled: t['--tx-dim'],
    },
    background: {
      default: t['--ink-900'],  // canvas
      paper: t['--ink-800'],    // cards
    },
    divider: t['--line'],
    /*
     * Disabled state, pinned rather than computed.
     *
     * MUI derives `action.disabled` from `mode`, and its light value is rgba(0,0,0,0.26) -- which
     * is 1.88:1 on a white card, below even the 3:1 that disabled text is allowed to sit at. It
     * went unnoticed for as long as there was one theme, because the dark default happens to land
     * around 3.6:1. --tx-dim is the token solved for exactly this question, in both themes, and
     * guardrail 5 holds it to 3:1.
     */
    action: {
      disabled: t['--tx-dim'],
      disabledBackground: t['--ink-700'],
    },
  },
  /**
   * Aligned to the SCSS breakpoint variables in styles/design-system.scss
   * ($bp-sm/md/lg/xl). MUI's defaults (600/900/1200/1536) disagreed with them,
   * so `theme.breakpoints.down('md')` and an `@include phone` block covered
   * different widths — one scale, not two.
   */
  breakpoints: {
    values: { xs: 0, sm: 480, md: 768, lg: 1024, xl: 1280 },
  },
  shape: {
    borderRadius: 8,
  },
  typography: {
    fontFamily: "'Lato', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    // Display headings use Montserrat; sizes tuned for a dense internal console.
    h1: { fontFamily: "'Montserrat', sans-serif", fontSize: 'var(--font-size-2xl)', fontWeight: 800, lineHeight: 1.2, letterSpacing: 'var(--ls-title)' },
    h2: { fontFamily: "'Montserrat', sans-serif", fontSize: 'var(--font-size-xl)', fontWeight: 800, lineHeight: 1.2, letterSpacing: 'var(--ls-title)' },
    h3: { fontFamily: "'Montserrat', sans-serif", fontSize: 'var(--font-size-md)', fontWeight: 700, lineHeight: 1.25, letterSpacing: 'var(--ls-title)' },
    h4: { fontFamily: "'Montserrat', sans-serif", fontSize: 'var(--font-size-base)', fontWeight: 700, lineHeight: 1.3 },
    h5: { fontFamily: "'Montserrat', sans-serif", fontSize: 'var(--font-size-sm)', fontWeight: 700, lineHeight: 1.35 },
    h6: { fontFamily: "'Montserrat', sans-serif", fontSize: 'var(--font-size-2xs)', fontWeight: 700, lineHeight: 1.4 },
    subtitle1: { fontSize: 'var(--font-size-sm-plus)', fontWeight: 700, lineHeight: 1.5 },
    subtitle2: { fontSize: 'var(--font-size-2xs)', fontWeight: 700, lineHeight: 1.5 },
    body1: { fontSize: 'var(--font-size-2xs)', fontWeight: 400, lineHeight: 1.5 },
    body2: { fontSize: 'var(--font-size-2xs)', fontWeight: 400, lineHeight: 1.5 },
    caption: { fontSize: 'var(--font-size-3xs)', fontWeight: 400, lineHeight: 1.5 },
    overline: { fontSize: 'var(--font-size-3xs)', fontWeight: 700, lineHeight: 1.5, letterSpacing: 'var(--ls-label)', textTransform: 'uppercase' },
    button: { fontSize: 'var(--font-size-2xs)', fontWeight: 700, textTransform: 'none', letterSpacing: 'var(--ls-caps)' },
  },
  /*
   * MUI wants 25 entries and uses them verbatim as `box-shadow`, so unlike `palette` these CAN be
   * custom properties -- nothing computes over them. That is worth taking: it was 25 hardcoded
   * near-black strings which would have needed a second hardcoded set per mode, and on light,
   * elevation genuinely comes from shadow rather than from surface steps, so the two sets are not
   * a tweak apart.
   */
  shadows: [
    'none',
    ...Array(3).fill('var(--shadow-sm)'),
    ...Array(5).fill('var(--shadow-md)'),
    ...Array(16).fill('var(--shadow-lg)'),
  ] as unknown as Shadows,
  /*
   * MUI animates dialogs, menus, popovers and drawers through JS (Grow, Fade, Slide), reading
   * these durations at theme construction. A CSS `prefers-reduced-motion` rule cannot reach them,
   * so before this key existed every modal in the console animated regardless of the setting —
   * and modals are the surfaces where a slide is most likely to be noticed.
   *
   * `matchMedia` is read once, at module load. The setting is an OS-level preference that
   * effectively never changes mid-session, and MUI cannot re-read the theme per render anyway.
   */
  transitions: {
    duration: (() => {
      const reduce =
        typeof window !== 'undefined' &&
        window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
      return reduce
        ? {
            shortest: 0,
            shorter: 0,
            short: 0,
            standard: 0,
            complex: 0,
            enteringScreen: 0,
            leavingScreen: 0,
          }
        : {
            // Aligned to --transition-fast / --transition-normal rather than MUI's defaults, so a
            // dialog and a hover state agree on how fast this console moves.
            shortest: 120,
            shorter: 150,
            short: 200,
            standard: 250,
            complex: 300,
            enteringScreen: 200,
            leavingScreen: 150,
          };
    })(),
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: 'var(--ink-900)',
          color: 'var(--tx-hi)',
        },
      },
    },
    MuiButton: {
      defaultProps: {
        disableElevation: true,
        /*
         * `compact` by default, because that is what the console overwhelmingly is.
         *
         * Measured: of 202 raw <Button>s, 135 pass size="small" and 67 pass nothing, so two
         * thirds of the console asked for the 30px button and a third silently got the 35px one.
         * Side by side that is a 5px step between neighbours, which is what "the buttons look
         * inconsistent" turned out to mean.
         *
         * design-system.scss defines both on purpose -- `action` is the page-level button,
         * `compact` is an affordance inside a card -- so this is not a claim that 35px is wrong.
         * It is a claim about which one a call site that expressed no opinion should get, and the
         * answer is the one the other two thirds chose. A genuinely page-level button says
         * size="medium" and means it.
         *
         * Since 2026-10-07 the choice is type and inset only: every size is --control-h tall
         * (34 / 40 / 44 by tier), so a medium button beside a small one no longer steps up.
         */
        size: 'small',
      },
      styleOverrides: {
        // The single button spec, from tokens. Every value that used to be
        // written here in px now lives in design-system.scss alongside the
        // hand-rolled buttons that have to match it.
        root: {
          borderRadius: 'var(--btn-radius)',
          padding: 'var(--btn-padding)',
          minHeight: 'var(--btn-min-height)',
          fontFamily: 'var(--font-family-primary)',
          fontSize: 'var(--btn-font-size)',
          fontWeight: 'var(--btn-weight)',
          textTransform: 'none',
          lineHeight: 1.5,
          transition: 'background-color 0.15s ease-out, color 0.15s ease-out, border-color 0.15s ease-out, transform 0.15s ease-out',
          '& .MuiTouchRipple-root': { opacity: 0.08 },
          '& .MuiButton-startIcon': { marginRight: 'var(--btn-icon-gap)', marginLeft: 0 },
          '& .MuiButton-endIcon': { marginLeft: 'var(--btn-icon-gap)', marginRight: 0 },
          '& .MuiButton-startIcon .MuiSvgIcon-root, & .MuiButton-endIcon .MuiSvgIcon-root': {
            fontSize: 'var(--btn-icon-size)',
          },
        },
        contained: {
          boxShadow: 'none',
          // A transparent border so a contained button occupies the same box
          // as the outlined one beside it. Without it, Cancel stood 2px taller
          // than Create because only one of them had a border.
          border: '1px solid transparent',
          '&:hover': {
            boxShadow: 'var(--shadow-primary)',
            transform: 'translateY(-1px)',
          },
          '&:active': { transform: 'translateY(0)' },
        },
        outlined: {
          // Default (neutral): hairline border, transparent fill — Cancel/secondary
          borderColor: 'var(--line)',
          color: 'var(--tx-mid)',
          backgroundColor: 'transparent',
          '&:hover': {
            borderColor: 'var(--acc-line)',
            backgroundColor: 'var(--ink-750)',
            color: 'var(--tx-hi)',
          },
          '&:active': { backgroundColor: 'var(--ink-700)' },
          // Color-specific outlined → filled, matching contained weight
          '&.MuiButton-outlinedPrimary': {
            backgroundColor: 'var(--brand)',
            borderColor: 'var(--brand)',
            color: 'var(--color-text-inverse)',
            '&:hover': { backgroundColor: 'var(--brand-hi)', borderColor: 'var(--brand-hi)', color: 'var(--color-text-inverse)' },
          },
          '&.MuiButton-outlinedSecondary': {
            backgroundColor: 'transparent',
            borderColor: 'var(--acc-line)',
            color: 'var(--teal)',
            '&:hover': { backgroundColor: 'var(--teal-tint)', borderColor: 'var(--teal)', color: 'var(--teal)' },
          },
          /* `colorError`, not `outlinedError`: MUI v9 dropped the compound variant+colour classes,
             so the old selector matched nothing and every destructive outlined button in the
             console rendered as a neutral grey one. Scoped to `outlined` by the slot it sits in.
             The sibling Primary / Secondary / Success selectors are just as dead; they are left
             alone here because reviving Primary would fill every default outlined button. */
          '&.MuiButton-colorError': {
            backgroundColor: 'transparent',
            borderColor: 'var(--red-line)',
            color: 'var(--red)',
            '&:hover': { backgroundColor: 'var(--red-tint)', borderColor: 'var(--red)' },
          },
          '&.MuiButton-outlinedSuccess': {
            backgroundColor: 'transparent',
            borderColor: 'var(--green-line)',
            color: 'var(--green)',
            '&:hover': { backgroundColor: 'var(--green-tint)', borderColor: 'var(--green)' },
          },
        },
        text: {
          // Same transparent border as contained, for the same reason: all
          // three variants have to occupy one box or a toolbar mixing them
          // steps up and down.
          border: '1px solid transparent',
          color: 'var(--tx-mid)',
          '&:hover': { backgroundColor: 'var(--ink-750)', color: 'var(--tx-hi)' },
          // contained and outlined both had a pressed state; text did not. On a phone that made
          // it the one button variant that acknowledged nothing.
          '&:active': { backgroundColor: 'var(--ink-700)' },
        },
        // size="small" is not a rare choice here — 89 of ~110 call sites pass
        // it, mostly by copying a neighbour. So it is the compact spec rather
        // than a one-off, and it lines up with the in-card buttons.
        sizeSmall: {
          padding: 'var(--btn-padding-sm)',
          minHeight: 'var(--btn-min-height-sm)',
          fontSize: 'var(--btn-font-size-sm)',
          '& .MuiButton-startIcon .MuiSvgIcon-root, & .MuiButton-endIcon .MuiSvgIcon-root': {
            fontSize: 'var(--btn-icon-size-sm)',
          },
        },
        // Wider inset and larger type, same height: size never changes how tall a button is.
        sizeLarge: { padding: '0 20px', minHeight: 'var(--control-h)', fontSize: 'var(--font-size-2xs)' },
      },
    },
    // A square target with a fixed icon inside. Sizing both is the point: the
    // spec used to set neither, so the height came out as MUI's padding plus
    // whatever icon size the call site happened to pass, and the console ended
    // up with seven different icon buttons.
    MuiIconButton: {
      styleOverrides: {
        root: {
          width: 'var(--icon-btn-size)',
          height: 'var(--icon-btn-size)',
          padding: 0,
          borderRadius: 8,
          flexShrink: 0,
          color: 'var(--tx-mid)',
          transition: 'background-color 0.15s ease-out, color 0.15s ease-out',
          '& .MuiSvgIcon-root': { fontSize: 'var(--icon-btn-icon)' },
          '& .MuiTouchRipple-root': { opacity: 0.08 },
          '&:hover': { backgroundColor: 'var(--ink-750)', color: 'var(--tx-hi)' },
          '&:active': { backgroundColor: 'var(--ink-700)' },
        },
        // `color` has to survive `root`'s neutral ink, or every color="error" delete and every
        // color="primary" action in the console renders the same grey (found 2026-10-06: the
        // destructive icons on Free Job Alerts and External Jobs were indistinguishable).
        colorPrimary: {
          color: 'var(--acc)',
          '&:hover': { color: 'var(--acc-hi)', backgroundColor: 'var(--acc-tint)' },
        },
        colorError: {
          color: 'var(--red)',
          '&:hover': { color: 'var(--red)', backgroundColor: 'var(--red-tint)' },
        },
        sizeSmall: {
          width: 'var(--icon-btn-size-sm)',
          height: 'var(--icon-btn-size-sm)',
          '& .MuiSvgIcon-root': { fontSize: 'var(--icon-btn-icon-sm)' },
        },
        // The touch target. Say `size="large"` rather than writing 44px into
        // a component stylesheet, where it loses to this block anyway and
        // leaves a button 32 wide and 44 tall.
        sizeLarge: {
          width: 'var(--icon-btn-size-lg)',
          height: 'var(--icon-btn-size-lg)',
          '& .MuiSvgIcon-root': { fontSize: 'var(--icon-btn-icon-lg)' },
        },
      },
    },
    // The single chip spec, from tokens — see design-system.scss. Components
    // were restating font size, weight and height in `sx`, each slightly
    // differently, which is how one page's status chip ended up 500-weight
    // and 4px taller than the next page's.
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 'var(--chip-radius)',
          fontFamily: 'var(--font-family-primary)',
          fontWeight: 'var(--chip-weight)',
          fontSize: 'var(--chip-font-size)',
          height: 'var(--chip-height)',
          backgroundColor: 'var(--ink-750)',
          color: 'var(--tx-mid)',
        },
        // size="small" is pinned to the same spec rather than left to MUI's
        // 24px default. 89 call sites pass it and none of them meant anything
        // by it, so it must not be a second size in disguise.
        sizeSmall: {
          fontSize: 'var(--chip-font-size)',
          height: 'var(--chip-height)',
          '& .MuiChip-label': { padding: '0 var(--chip-padding-x)' },
        },
        label: { padding: '0 var(--chip-padding-x)' },
        outlined: { borderColor: 'var(--line)' },
      },
    },
    MuiTable: {
      styleOverrides: {
        root: { borderCollapse: 'separate', borderSpacing: 0 },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          position: 'sticky',
          top: 0,
          zIndex: 2,
          '& .MuiTableCell-root': {
            backgroundColor: 'var(--ink-850)',
            color: 'var(--tx-low)',
            fontFamily: "'Lato', sans-serif",
            fontWeight: 700,
            fontSize: 'var(--font-size-3xs)',
            textTransform: 'uppercase',
            letterSpacing: 'var(--ls-table)',
            padding: 'var(--table-cell-pad-y) var(--table-cell-pad-x)',
            borderBottom: '1px solid var(--line)',
            whiteSpace: 'nowrap',
          },
        },
      },
    },
    MuiTableBody: {
      styleOverrides: {
        root: {
          '& .MuiTableRow-root': {
            transition: 'background-color 0.12s ease-out',
            '&:hover': { backgroundColor: 'var(--ink-800)' },
            /*
             * A row is clickable and had no pressed state, so opening a record gave no
             * acknowledgement at all until the route changed. A background swap rather than the
             * scale `.cc-pressable` uses: scaling a table row distorts the grid it belongs to.
             * Zero transition on the way in so it lands on the frame the pointer goes down.
             */
            '&:active': { backgroundColor: 'var(--ink-750)', transitionDuration: '0s' },
            '&:last-child .MuiTableCell-root': { borderBottom: 'none' },
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          padding: 'var(--table-cell-pad-y) var(--table-cell-pad-x)',
          fontSize: 'var(--font-size-2xs)',
          color: 'var(--tx-hi)',
          borderBottom: '1px solid var(--line-soft)',
          lineHeight: 1.5,
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: { cursor: 'default' },
      },
    },
    MuiTablePagination: {
      styleOverrides: {
        root: { fontSize: 'var(--font-size-xs)', color: 'var(--tx-mid)', borderTop: '1px solid var(--line)' },
        selectLabel: { fontSize: 'var(--font-size-xs)' },
        displayedRows: { fontSize: 'var(--font-size-xs)', fontFamily: "'DM Mono', monospace" },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundColor: 'var(--ink-800)',
          backgroundImage: 'none',
          boxShadow: 'var(--shadow-sm)',
          borderRadius: 12,
          border: '1px solid var(--line)',
          transition: 'none',
        },
        elevation0: { boxShadow: 'none' },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundColor: 'var(--ink-800)',
          backgroundImage: 'none',
          borderRadius: 12,
          border: '1px solid var(--line)',
          boxShadow: 'var(--shadow-sm)',
          transition: 'none',
        },
      },
    },
    /**
     * THE field spec. Eight components accept text input; they used to
     * disagree on fill, border, padding (7px vs 8px) and font size
     * (14/13/12.5px), so any form built from more than one of them failed to
     * line up. This block is the only place that geometry is declared —
     * FormField, SelectFormField, AutocompleteFormField, PhoneInput,
     * LocationAutocomplete, SearchFilter, TextEditor and FileUpload all
     * inherit from here rather than restating it.
     */
    /*
     * Stars were the one control in the console outside the design system. There was no MuiRating
     * key and no .MuiRating rule anywhere, so MUI's stock palette applied: a hard-coded #faaf00
     * for a filled star and palette.action.disabled for an empty one. Neither is a token, so the
     * feedback card's rating did not match anything around it.
     *
     * --gold is the existing warm accent, already used for the AI badge and subscription
     * notifications, which makes it the established "this is notable" colour rather than a new one.
     */
    MuiRating: {
      styleOverrides: {
        iconFilled: {
          color: 'var(--gold)',
        },
        iconHover: {
          color: 'var(--gold)',
        },
        iconEmpty: {
          color: 'var(--tx-low)',
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          backgroundColor: 'var(--ink-750)',
          fontSize: 'var(--font-size-2xs)', // 13px
          transition: 'box-shadow 0.15s ease-out, border-color 0.15s ease-out',
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: 'var(--field-border)',
            borderWidth: 1,
          },
          '&:hover:not(.Mui-focused):not(.Mui-error):not(.Mui-disabled) .MuiOutlinedInput-notchedOutline': {
            borderColor: 'var(--acc-line)',
            borderWidth: 1,
          },
          '&.Mui-focused': { boxShadow: '0 0 0 3px var(--acc-tint)' },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: 'var(--acc)',
            borderWidth: 1.5,
          },
          '&.Mui-error .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--red)' },
          '&.Mui-error.Mui-focused': { boxShadow: '0 0 0 3px var(--red-tint)' },
          '&.Mui-disabled': {
            backgroundColor: 'var(--ink-800)',
            '& .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--line)' },
          },
          /*
           * Multiline. MUI pads the ROOT when multiline is set (8.5px 14px at size=small) and
           * expects the textarea inside it to carry none. Our `input` override below gives that
           * textarea 8px 11px, so both applied and the text sat about 16px from the top and 25px
           * from the left, against 8/11 everywhere else. On a note composer three lines tall that
           * reads as a box with a hole in it.
           *
           * The textarea's inset is the one to keep, because it is the same inset a single-line
           * field has, so the root's goes. This lived in FormField, which is why every multiline
           * field that was not a FormField had the bug.
           */
          '&.MuiInputBase-multiline': { padding: 0 },

          /*
           * Height: --control-h at every tier, the same token every button reads.
           *
           * The vertical inset is derived rather than stated: an <input> is 1.4375em tall (MUI's
           * own line box, and the Select below is matched to it), so (control-h - 1.4375em) / 2
           * top and bottom makes the box exactly --control-h whatever the font size. 8px was right
           * only at 12.5px on a desktop -- on a tablet the field came out 35.4px beside a 40px
           * button, and on a phone 36px beside a 44px one, because nothing scaled the field.
           *
           * A textarea takes the same inset, so its first line sits where a single-line field's
           * text does, and grows with its rows; an autocomplete with chips is floored at
           * --control-h in AutocompleteFormField and grows. (0,3,0) on purpose: it has to beat
           * the Select rule below, which states the 8px shorthand at (0,2,0).
           */
          '&.MuiOutlinedInput-root .MuiOutlinedInput-input': {
            paddingTop: 'var(--field-pad-y)',
            paddingBottom: 'var(--field-pad-y)',
          },

          /*
           * Select: right padding widened to clear the dropdown arrow.
           *
           * lineHeight 1.4375, not 1.5, and that is the whole of why a dropdown used to stand a
           * pixel taller than the text field beside it on every form in the console. MUI sizes an
           * <input> with `height: 1.4375em`; a Select is a div, so its box is whatever the line
           * box is. At 12.5px, 1.5 is 18.75px against the input's 17.97px -- 0.78px, which rounds
           * to a visible 1px, 34 against 35, on /jobs/new, /companies/new, /user-rbac/new and
           * every workspace. Matching the ratio makes the two boxes the same height by
           * construction rather than by luck.
           */
          '& .MuiSelect-select': { padding: '8px 32px 8px 11px', minHeight: 'unset', lineHeight: 1.4375 },

          /*
           * Chrome paints autofilled inputs with its own opaque light-blue fill and near-black
           * text, and it wins: the fill is a UA style that ordinary background-color cannot
           * override. On a dark console that turns one field bright blue while its neighbour
           * stays graphite, which is what an autofilled email looked like next to a phone.
           *
           * The long inset shadow is the standard way round it — the fill is painted behind the
           * shadow, so a large enough one in the field's own colour covers it. `background-clip`
           * keeps the text colour from being reset with it.
           */
          '& input:-webkit-autofill, & input:-webkit-autofill:hover, & input:-webkit-autofill:focus, & input:-webkit-autofill:active':
            {
              WebkitBoxShadow: '0 0 0 1000px var(--ink-750) inset',
              WebkitTextFillColor: 'var(--tx-hi)',
              caretColor: 'var(--tx-hi)',
              borderRadius: 'inherit',
              transition: 'background-color 9999s ease-out 0s',
            },

          /*
           * Adornments render INSIDE the root, alongside the input, so the
           * root has to own the inset on that side. Without this the icon
           * sits hard against the border while the text keeps its own 11px,
           * which reads as a misaligned field.
           */
          '&.MuiInputBase-adornedStart': {
            paddingLeft: '11px',
            '& .MuiOutlinedInput-input': { paddingLeft: 0 },
          },
          '&.MuiInputBase-adornedEnd': {
            paddingRight: '11px',
            '& .MuiOutlinedInput-input': { paddingRight: 0 },
          },
        },
        input: {
          padding: '8px 11px',
          fontSize: 'var(--font-size-2xs)',
          fontWeight: 400,
          lineHeight: 1.5,
          color: 'var(--tx-hi)',
          '&::placeholder': { color: 'var(--tx-low)', opacity: 1 },
          '&.Mui-disabled': {
            color: 'var(--tx-dim)',
            WebkitTextFillColor: 'var(--tx-dim)',
          },
        },
        notchedOutline: { borderColor: 'var(--field-border)' },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: { fontSize: 'var(--font-size-2xs)', color: 'var(--tx-hi)' },
        input: { fontSize: 'var(--font-size-2xs)', lineHeight: 1.5 },
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        root: {
          color: 'var(--tx-mid)',
          fontSize: 'var(--font-size-2xs)',
          fontWeight: 700,
          '&.Mui-focused': { color: 'var(--acc)' },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: { color: 'var(--tx-low)', fontSize: 'var(--font-size-2xs)' },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: { color: 'var(--tx-low)', fontSize: 'var(--font-size-3xs)', marginTop: '4px' },
      },
    },
    /**
     * Alerts. This block is not cosmetic — without it the text was unreadable
     * on every page that renders one.
     *
     * MUI derives a standard Alert's colours from `palette[severity].light`:
     * in dark mode `color: lighten(light, 0.6)` and
     * `backgroundColor: darken(light, 0.9)`. Both manipulators PRESERVE the
     * alpha channel. Our semantic `.light` entries are tints — `warning.light`
     * is `rgba(224,165,74,0.10)` — because they are consumed as chip and badge
     * fills elsewhere. So the computed text colour came out as
     * `rgba(242,219,182,0.1)`: the right hue at ten percent opacity, i.e. a
     * message rendered at 10% against the canvas. The banner looked present
     * and could not be read.
     *
     * Severity is carried by the icon, border and fill; the message itself is
     * --tx-hi like every other body string in the console. Do not go back to
     * tinting the text — that is what produced a legibility bug that shipped.
     */
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          border: '1px solid transparent',
          padding: '8px 14px',
          fontFamily: 'var(--font-family-primary)',
          fontSize: 'var(--font-size-2xs)',
          lineHeight: 1.5,
          alignItems: 'center',
        },
        icon: {
          padding: 0,
          marginRight: 10,
          opacity: 1,
          '& .MuiSvgIcon-root': { fontSize: '1.125rem' },
        },
        message: { padding: 0, minWidth: 0 },
        action: { padding: 0, marginRight: 0, alignItems: 'center' },
      },
      variants: alertVariants,
    },
    MuiAlertTitle: {
      styleOverrides: {
        root: {
          fontFamily: "'Montserrat', sans-serif",
          fontSize: 'var(--font-size-2xs)',
          fontWeight: 700,
          lineHeight: 1.4,
          marginBottom: 2,
          color: 'inherit',
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: 'var(--ink-700)',
          color: 'var(--tx-hi)',
          fontSize: 'var(--font-size-3xs)',
          borderRadius: 6,
          padding: '4px 10px',
          border: '1px solid var(--line)',
        },
        arrow: { color: 'var(--ink-700)' },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: { borderColor: 'var(--line)' },
      },
    },
    /**
     * Menus, popovers and autocomplete listboxes. These used to be a global
     * !important block in SelectFormField/index.scss carrying light-theme
     * values; they belong here so a page can still override them.
     *
     * Two rules the old block broke, restated: on dark, hover must always move
     * TOWARD light (it used --ink-850, DARKER than the --ink-800 menu paper, so
     * the row receded instead of lifting); and the selected option must keep
     * its emphasis (it forced every option to --tx-hi, flattening the
     * distinction away).
     */
    MuiMenu: {
      styleOverrides: {
        paper: {
          backgroundColor: 'var(--ink-800)',
          border: '1px solid var(--line)',
          boxShadow: 'var(--shadow-md)',
          borderRadius: 8,
          padding: '4px 0',
        },
      },
    },
    MuiPopover: {
      styleOverrides: {
        paper: {
          backgroundColor: 'var(--ink-800)',
          border: '1px solid var(--line)',
          boxShadow: 'var(--shadow-md)',
          borderRadius: 8,
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          fontSize: 'var(--font-size-2xs)',
          fontWeight: 400,
          lineHeight: 1.4,
          padding: '8px 14px',
          minHeight: 38,
          color: 'var(--tx-mid)',
          '&:hover, &.Mui-focusVisible': {
            backgroundColor: 'var(--ink-750)',
            color: 'var(--tx-hi)',
          },
          '&.Mui-selected': {
            backgroundColor: 'var(--acc-tint)',
            color: 'var(--acc)',
            fontWeight: 700,
            '&:hover, &.Mui-focusVisible': { backgroundColor: 'var(--acc-tint)', color: 'var(--acc)' },
          },
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        select: { fontSize: 'var(--font-size-2xs)' },
        icon: { color: 'var(--tx-mid)' },
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        // MUI gives the autocomplete's input root its own padding on top of
        // the field spec, which made every location and skill picker 1px
        // taller than the plain text field beside it. The field spec owns the
        // inset; this just stops the double-count.
        // MUI pads the autocomplete's input root itself, on top of the field
        // spec, which left every location, skill and role picker 1px taller
        // and one step larger than the plain text field beside it.
        //
        // It has to be reset from the `root` slot, not `inputRoot`: MUI writes
        // the rule as a DESCENDANT selector (`.MuiAutocomplete-root
        // .MuiOutlinedInput-root`), and an override on the element itself
        // never reaches it however specific it looks.
        // Font size and leading only.
        //
        // MUI also pads the autocomplete's input root itself, which leaves it
        // 1px taller than a plain text field. That padding is emitted through
        // the component's `variants`, after styleOverrides in the same emotion
        // sheet, so it wins on order; `&`, `&&`, a stylesheet rule three
        // classes deep and an !important were each ignored or made it worse
        // (the last one double-padded the field and pushed the text 8px in).
        //
        // Left alone deliberately. 1px of height is invisible next to a
        // mismatched font size, which is what this actually fixes — the
        // pickers rendered at 14px beside 13px fields.
        inputRoot: {
          '& .MuiAutocomplete-input': {
            // The field spec's own size. It said --fs-body, which is half a pixel larger than
            // what MuiOutlinedInput gives a plain field, so a picker read as a size apart from
            // the field beside it. The padding is settled in AutocompleteFormField's stylesheet;
            // see the note there for why it cannot be done from here.
            fontSize: 'var(--font-size-2xs)',
            lineHeight: 1.5,
          },
        },
        paper: {
          backgroundColor: 'var(--ink-800)',
          boxShadow: 'var(--shadow-md)',
          border: '1px solid var(--line)',
          borderRadius: 8,
        },
        listbox: { padding: '4px 0' },
        option: {
          fontSize: 'var(--font-size-2xs)',
          fontWeight: 400,
          lineHeight: 1.4,
          padding: '8px 14px',
          minHeight: 38,
          color: 'var(--tx-mid)',
          '&.Mui-focused': { backgroundColor: 'var(--ink-750)', color: 'var(--tx-hi)' },
          '&[aria-selected="true"]': {
            backgroundColor: 'var(--acc-tint)',
            color: 'var(--acc)',
            fontWeight: 700,
            '&.Mui-focused': { backgroundColor: 'var(--acc-tint)', color: 'var(--acc)' },
          },
        },
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          padding: 4,
          color: 'var(--field-border)',
          '& .MuiSvgIcon-root': { fontSize: '1.25rem' },
          '&.Mui-checked': { color: 'var(--acc)' },
          '&.Mui-disabled': { color: 'var(--tx-dim)' },
        },
      },
    },
    MuiFormControlLabel: {
      styleOverrides: {
        root: { marginLeft: 4 },
        label: { fontSize: 'var(--font-size-2xs)', lineHeight: 1.5, color: 'var(--tx-hi)' },
      },
    },
    /*
     * Switches. There was no block here, so all 17 in the console rendered MUI's stock
     * `primary` blue -- next to a themed Checkbox in the same form, on the same card. The
     * checkbox below has been themed since the palette landed; the switch was simply missed,
     * and nothing looks at a colour and asks whether it came from the design system.
     *
     * `--acc` when on, matching MuiCheckbox, so the two read as one family.
     */
    MuiSwitch: {
      styleOverrides: {
        root: { width: 42, height: 26, padding: 0, margin: 4 },
        switchBase: {
          padding: 3,
          color: 'var(--tx-low)',
          '&.Mui-checked': {
            color: 'var(--ink-950)',
            transform: 'translateX(16px)',
            '& + .MuiSwitch-track': { backgroundColor: 'var(--acc)', opacity: 1 },
          },
          '&.Mui-disabled': { color: 'var(--tx-dim)' },
          '&.Mui-disabled + .MuiSwitch-track': { opacity: 0.4 },
          // The ripple is a 40px circle around a 20px thumb; on a dense settings row it reads
          // as the control having a stray hover state of its own.
          '&:hover': { backgroundColor: 'transparent' },
        },
        thumb: { width: 20, height: 20, boxShadow: 'none' },
        track: {
          borderRadius: 13,
          backgroundColor: 'var(--ink-700)',
          border: '1px solid var(--field-border)',
          opacity: 1,
        },
      },
    },
    /*
     * Toggle button groups. Also unthemed, and the gap was filled four separate times in
     * EmailAnalytics, Dashboard, EmailAudit and Panel -- four stylesheets each inventing a
     * selected state for the same control. Owning it here lets those go.
     */
    MuiToggleButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontSize: 'var(--font-size-2xs)',
          fontWeight: 600,
          color: 'var(--tx-mid)',
          borderColor: 'var(--line)',
          // The control height, as a button: no vertical inset, the label centres in the box.
          // `6px 12px` on MUI's 1.75 line box made a medium toggle 36px beside 34px fields.
          padding: '0 12px',
          minHeight: 'var(--control-h)',
          lineHeight: 1.5,
          '&:hover': { backgroundColor: 'var(--ink-750)' },
          '&.Mui-selected': {
            color: 'var(--tx-hi)',
            backgroundColor: 'var(--acc-tint)',
            borderColor: 'var(--acc-line)',
            '&:hover': { backgroundColor: 'var(--acc-tint)' },
          },
        },
        // The compact button spec, as MuiButton's sizeSmall. Without it a small toggle came out
        // at 36px (this padding plus MUI's 1.75 line box) beside 30px small buttons, which the
        // button-height check in workflows.spec catches as soon as one sits on a page it visits.
        sizeSmall: {
          padding: 'var(--btn-padding-sm)',
          minHeight: 'var(--control-h)',
          fontSize: 'var(--btn-font-size-sm)',
          lineHeight: 1.5,
        },
      },
    },
    MuiToggleButtonGroup: {
      styleOverrides: {
        root: { backgroundColor: 'var(--ink-850)' },
      },
    },

    MuiBadge: {
      styleOverrides: {
        badge: { fontSize: 'var(--font-size-3xs)', fontWeight: 700 },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: { borderRadius: 4, backgroundColor: 'var(--ink-700)' },
      },
    },
    MuiCircularProgress: {
      defaultProps: { size: 24 },
    },
  },
});
}

/**
 * The dark theme, for the two places that still want a ready-made one: the Vitest render helper
 * and any test that has no opinion about mode. The app itself calls createAppTheme through
 * ThemeModeProvider.
 */
export default createAppTheme('dark');
